import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { doGet } from '../services/http';
import { errorHandler, getAsyncThunkMsg } from '../services/Utils';

/**
 * Async thunk to fetch cafe data (ads, canteens, recommended items)
 * @returns {Promise<Object>} Cafe data
 */
export const fetchCafe = createAsyncThunk('cafe/fetchCafe', async (_, { rejectWithValue }) => {
  try {
    const response = await doGet('cafe');
    return response.data;
  } catch (err) {
    console.error(err);
    errorHandler(err);
    return rejectWithValue(getAsyncThunkMsg(err));
  }
});

/**
 * Redux slice for cafe/home screen state
 * Manages advertisements, canteens list, and recommended items
 */
const CafeSlice = createSlice({
  name: 'cafe',
  initialState: {
    ads: [],
    canteens: [],
    recommendedItems: [],
    status: 'idle',
    error: null,
    refreshing: false,
    enable_bulk_orders: false,
  },
  reducers: {
    enableRefreshControl(state, action) {
      if (!action.payload?.retry) {
        state.refreshing = true;
      }
    },
    disableRefreshControl(state, action) {
      state.refreshing = false;
    },
    setAds(state, action) {
      state.ads = action.payload;
    },
    setBulkOrderStatus(state, action) {
      state.enable_bulk_orders = action.payload;
    },
    setCanteens(state, action) {
      state.canteens = action.payload;
    },
    updateCanteen(state, action) {
      state.canteens[action.payload?.index] = action.payload?.restaurant;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCafe.pending, (state, action) => {
      if (!state.refreshing) {
        state.status = 'loading';
      }
    })
      .addCase(fetchCafe.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.ads = action.payload?.ads;
        state.canteens = action.payload?.canteens;
        state.recommendedItems = action.payload?.recommendedItems || [];
        state.refreshing = false;
      })
      .addCase(fetchCafe.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action?.payload;
        state.refreshing = false;
      });
  },
});

export const { setAds, setCanteens, setBulkOrderStatus, enableRefreshControl, updateCanteen } =
  CafeSlice.actions;
export default CafeSlice.reducer;
